import {
    ButtonType,
    ButtonWidget,
    CheckBoxWithLabelWidget,
    CompactInlineAlertWidget,
    DialogFooterButtonsWidget,
    DialogPositioningBehavior,
    DialogType,
    DialogWidget,
    DropdownButtonType,
    ExpandoDirection,
    ExpandoWidget,
    InlineAlertType,
    List,
    ListItem,
    RadioButtonWithLabelGroupWidget,
    RadioButtonWithLabelOption,
    SingleSelectDropdown,
    TextFieldWidget,
    TextLinkKind,
    TextLinkWidget
} from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { CommonErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

import { sanitize } from '../../../../src/commonInfra/utils/Sanitize';
import { SiteUsers } from '../../../../src/userManagement/services/resources/SiteUsers';
import { siteUrlsSelector } from '../../../bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { openReactConfirmationDialog } from '../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { WindowLocationService } from '../../../commonInfra/services/WindowLocationService';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { ISiteUser } from '../../../userManagement/users/serverApi/types/ISiteUser';
import { IUpdateSiteSettingsForSiteAdminParams, UpdateSiteSettingsForSiteAdminRequest } from '../../ServerApi/requests/siteSettings/UpdateSiteSettingsForSiteAdminRequest';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import { OIDCAuthenticationProvider, OIDCProviderProps } from '../directives/OIDCAuthenticationProvider';
import { SAMLAuthenticationProvider, SAMLProviderProps } from '../directives/SAMLAuthenticationProvider';
import {
    AuthenticationProviderType,
    IAuthenticationProvider,
    IOIDCAuthenticationProvider,
    is3rdParty,
    ISAMLAuthenticationProvider,
    ISiteAuthenticationSettings,
    SiteAuthenticationSettingsService
} from '../services/SiteAuthenticationSettingsService';
import { dropDownWidgetDescriptionText, dropDownWidgetListItem, dropDownWidgetTitleText } from '../styles/ExternalAuthenticationProviderStyle';
import { samlProviderAlertText, samlProviderGroupContent, samlProviderGroupNumber, samlProviderGroupSettings, samlProviderMatchAttributesTitleStyle, samlProviderTextDescriptionStyle, samlProviderTextTitleStyle } from '../styles/SAMLAuthenticationProviderStyle';
import { authDropdownStyle, expandoStyle2 } from '../styles/SiteAuthenticationSettingsStyles';
import { ConfirmActionDialogSiteAuthenticationProviderEnable } from './ConfirmActionDialogSiteAuthenticationProviderEnable';
import { AuthenticationColumnKeys, AuthenticationTypesGrid } from './multipleIDPComponents/AuthenticationTypesGrid';
import { MaxConfigurationsReachedDialog } from './multipleIDPComponents/MaxConfigurationsReachedDialog';
import { ScimSettings } from './ScimSettings';


export interface SiteAuthenticationSettingsProps {
    showEncryptionStatus: boolean;
    server: SessionManagementService;
    hideNameAndId: boolean;
    siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin;
    updateSiteSettingsForSiteAdmin?: () => void;
}

export interface SiteAuthenticationSettingsState {
    // SiteAuthenticationSettings in state and not taken from props, to be able to set radio buttons on mount
    siteAuthenticationSettings: ISiteAuthenticationSettings;
    isConnectedClientsVisible: boolean;
    isEmbeddedVizForSiteSamlSupported: boolean;
    isExternalProviderEnabled: boolean;
    isExternalProviderEnabledChanged: () => void;
    isTableauIdSupported: boolean;
    usersUrl: string;
    getProviderDisplayName: (provider: IAuthenticationProvider) => string;
    getProviderDescription: (provider: IAuthenticationProvider) => string;
    download: (evt: Event, url: string) => void;
    getHelp: (category?: string) => void;
    aboutToBeDisabled: IAuthenticationProvider[];
    aboutToLockMyselfOut: boolean;
    defaultAuthEmbeddedViewsButtons: RadioButtonWithLabelOption<string>[];
    authenticationTypesProviderButtons: RadioButtonWithLabelOption<string>[];
    authenticationTypesDropDownListItems: ListItem[];
    selectedProvider: IAuthenticationProvider;
    userAttributes: IUserAttributes;
    defaultAuthEmbeddedViewsRadioButtonIdx: number;
    authenticationTypesRadioButtonIdx: number;
    showEditConnection: boolean;
    samlProvider: ISAMLAuthenticationProvider;
    samlProviderProps: SAMLProviderProps;
    samlMetadataFile: File;
    isSamlMetadataFileChanged: boolean;
    showOIDCConfiguration: boolean;
    oidcProvider: IOIDCAuthenticationProvider;
    oidcProviderProps: OIDCProviderProps;
    showSalesforceDomain: boolean;
    savedSalesforceDomain: string;
}

interface SalesforceProviderConfigurerProps {
    provider: IOIDCAuthenticationProvider;
}

const SalesforceProviderConfigurer: React.FC<SalesforceProviderConfigurerProps> = props => {
    const ZERO_WIDTH = '\u200B';
    const SALESFORCE_DOMAIN_PLACEHOLDER = 'login.salesforce.com';

    const [dialogShown, setDialogShown] = React.useState<boolean>();
    const [domainFieldMessage, setDomainFieldMessage] = React.useState<string>(ZERO_WIDTH);
    const [domainFieldValid, setDomainFieldValid] = React.useState<boolean | 'pending'>();
    const [editedProvider, setEditedProvider] = React.useState<IOIDCAuthenticationProvider>({ ...props.provider });

    function cancel() {
        setDomainFieldMessage(ZERO_WIDTH);
        setDomainFieldValid(undefined);
        setEditedProvider({ ...props.provider });
        setDialogShown(false);
    }

    function apply() {
        setDomainFieldValid('pending');
        SiteAuthenticationSettingsService.saveOIDCProvider(editedProvider)
            .then(result => {
                if (result.errors && result.errors.length) {
                    setDomainFieldMessage(Messages.invalidValue());
                    setDomainFieldValid(false);
                } else {
                    Object.assign(props.provider, editedProvider);
                    setDomainFieldMessage(ZERO_WIDTH);
                    setDomainFieldValid(undefined);
                    setDialogShown(false);
                }
            });
    }

    return <>
        <div>{Messages.ssoAuthTypeOIDC_description_salesforce({ DOMAIN: props.provider.salesforceDomain || SALESFORCE_DOMAIN_PLACEHOLDER })}</div>
        <TextLinkWidget
            children={Messages.salesforceEditMyDomain()}
            kind={TextLinkKind.Standard}
            onClick={e => {
                setDialogShown(true);
                e.preventDefault();
            }}
            testId='salesforce-provider-configurer-link'
        />
        {dialogShown
            ? <DialogWidget
                isModal={true}
                onCloseRequest={cancel}
                positioningBehavior={DialogPositioningBehavior.Draggable}
                showCloseButton={true}
                testId='salesforce-provider-configurer-dialog'
                title={Messages.salesforce()}
                type={DialogType.NormalWithTitleBar}
            >
                <TextFieldWidget
                    kind='line'
                    label={Messages.salesforceMyDomain()}
                    message={domainFieldMessage}
                    onClear={() => {
                        setDomainFieldMessage(ZERO_WIDTH);
                        setDomainFieldValid(undefined);
                        setEditedProvider({ ...editedProvider, salesforceDomain: '' });
                    }}
                    onChange={e => setEditedProvider({ ...editedProvider, salesforceDomain: e.target.value })}
                    placeholder={SALESFORCE_DOMAIN_PLACEHOLDER}
                    required={false}
                    style={{ width: 200 }}
                    testId='salesforce-domain-field-text'
                    valid={domainFieldValid}
                    value={editedProvider.salesforceDomain || ''}
                />
                <DialogFooterButtonsWidget
                    buttons=
                    {[
                        {
                            buttonType: ButtonType.Outline,
                            children: Messages.cancel(),
                            handleClick: cancel,
                            testId: 'salesforce-provider-configurer-dialog-footer-cancel',
                        },
                        {
                            buttonType: ButtonType.Outline,
                            children: Messages.apply(),
                            handleClick: apply,
                            testId: 'salesforce-provider-configurer-dialog-footer-apply',
                        },
                    ]}
                    testId='salesforce-provider-configurer-dialog-footer'
                />
            </DialogWidget>
            : null
        }
    </>;
};

const SALESFORCE_DOMAIN_PLACEHOLDER = 'login.salesforce.com';
const MAX_LIMIT_ON_TOTAL_ALLOWED_CONFIGURATIONS = 20;
const DEFAULT_IDP_CONFIG_ID_OIDC = '00000000-0000-0000-0000-000000000000';
const DEFAULT_IDP_CONFIG_ID_SAML = '11111111-1111-1111-1111-111111111111';

interface IUserAttributes extends _.Dictionary<string> {}

export class SiteAuthenticationSettings extends React.Component<SiteAuthenticationSettingsProps, SiteAuthenticationSettingsState> {
    private messageListener: (evt: MessageEvent) => void;

    constructor(props: SiteAuthenticationSettingsProps) {
        super(props);
        this.state = {
            siteAuthenticationSettings: undefined,
            isConnectedClientsVisible: ServerSettingsUnauthenticated.isConnectedClientsVisible(),
            isEmbeddedVizForSiteSamlSupported: ServerSettingsUnauthenticated.isEmbeddedVizForSiteSamlSupported(),
            isExternalProviderEnabled: false,
            isExternalProviderEnabledChanged: () => {
                const newVal = !this.state.isExternalProviderEnabled;
                const provider = (newVal
                    ? this.state.siteAuthenticationSettings.externalProviders[0]
                    : this.state.siteAuthenticationSettings.defaultProvider);
                if (newVal) {
                    this.changeProvider('');
                } else {
                    this.enableProvider(provider.id, provider.idpConfigurationId).then(() => {
                        this.setState({ isExternalProviderEnabled: newVal });
                        this.setState({ selectedProvider: undefined });
                    });
                }
            },
            isTableauIdSupported: ServerSettingsUnauthenticated.isTableauIdSupported(),
            usersUrl: siteUrlsSelector(getAppStore().getState()).usersUrl(),
            getProviderDisplayName: (provider: IAuthenticationProvider) => {
                return provider
                    ? SiteAuthenticationSettingsService.getProviderDisplayName(provider)
                    : Messages.siteAuthenticationTypeSSO();
            },
            getProviderDescription: (provider: IAuthenticationProvider) => {
                if ((provider as IOIDCAuthenticationProvider)?.alias === 'OpenId Connect') {
                    return Messages.ssoAuthTypeOIDC_description_OpenID_Connect();
                }
                switch (provider.type) {
                    case AuthenticationProviderType.oidc:
                        return Messages.ssoAuthTypeOIDC_description();
                    case AuthenticationProviderType.saml:
                        return Messages.ssoAuthTypeSAML_description();
                    case AuthenticationProviderType.default:
                        return Messages.ssoAuthTypeDefault_description();
                }
            },
            getHelp: (category?: string) => {
                const url = HelpService.getServerHelpUrlByCategory(category);
                window.open(url, '_blank');
            },
            download: (evt: Event, url: string) => {
                evt.preventDefault();
                WindowLocationService.download(url);
            },
            aboutToBeDisabled: [],
            aboutToLockMyselfOut: false,
            defaultAuthEmbeddedViewsButtons: [],
            authenticationTypesProviderButtons: [],
            authenticationTypesDropDownListItems: [],
            selectedProvider: undefined,
            userAttributes: null,
            defaultAuthEmbeddedViewsRadioButtonIdx: 0,
            authenticationTypesRadioButtonIdx: 0,
            showEditConnection: false,
            samlProvider: undefined,
            samlProviderProps: {
                provider: undefined
            },
            samlMetadataFile: null,
            isSamlMetadataFileChanged: false,
            showOIDCConfiguration: false,
            oidcProvider: undefined,
            oidcProviderProps: {
                provider: undefined,
                showTestConfiguration: false,
                hasUpdated: false
            },
            showSalesforceDomain: false,
            savedSalesforceDomain: undefined
        };

        this.messageListener = (evt: MessageEvent) => this.receiveMessage(evt);
    }

    public componentDidMount() {
        // Fetch data and initialize radio buttons
        this.setupInitialState();
    }

    public componentWillUnmount() {
        // Fix warning: Can't perform a React state update on an unmounted component
        // Once component is about to be unmounted, the callback functions used by its children should not work,
        // since the children will not be rendered anyway
        this.setState = () => { return; };
    }

    private setupInitialState(refresh: boolean = false) {
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings(refresh).then((siteAuthSettings: ISiteAuthenticationSettings) => {
            this.setState({ siteAuthenticationSettings: siteAuthSettings });
            this.setState({ defaultAuthEmbeddedViewsButtons: this.getProviderButtons() });
            const samlProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider;
            this.setState({ samlProvider: {
                ...samlProvider,
                attributeMapping: { ...samlProvider.attributeMapping }
            }});
            this.setState({
                samlProviderProps: {
                    provider: this.state.samlProvider,
                    updateSamlProvider: this.updateSamlProvider,
                    updateEmbeddedOption: this.updateEmbeddedOption,
                    updateAllowFullName: this.updateAllowFullName,
                    updateFirstNameMapping: this.updateFirstNameMapping,
                    updateLastNameMapping: this.updateLastNameMapping,
                    updateFullNameMapping: this.updateFullNameMapping,
                    updateEmailMapping: this.updateEmailMapping,
                    updateSAMLMetadataFile: this.updateSAMLMetadataFile
                }
            });
            const provider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled));
            const oidcProvider = (provider != null && provider.type === AuthenticationProviderType.oidc) ? provider :
                _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'OIDC')) as IOIDCAuthenticationProvider;
            this.setState({ oidcProvider: oidcProvider as IOIDCAuthenticationProvider });
            this.setState({
                oidcProviderProps: {
                    provider: this.state.oidcProvider,
                    showTestConfiguration: this.state.oidcProvider == null ? false : this.state.oidcProvider.isEnabled,
                    hasUpdated: false,
                    updateClientId: this.updateOIDCClientId,
                    updateClientSecret: this.updateOIDCClientSecret,
                    updateConfigurationUrl: this.updateOIDCConfigurationUrl,
                    updateEmbeddedOption: this.updateOIDCEmbeddedOption,
                    updateUseFullName: this.updateOIDCUseFullName,
                    updateFirstNameMapping: this.updateOIDCFirstNameMapping,
                    updateLastNameMapping: this.updateOIDCLastNameMapping,
                    updateFullNameMapping: this.updateOIDCFullNameMapping,
                    updateEmailMapping: this.updateOIDCEmailMapping,
                    updateSingleLougout: this.updateOIDCSingleLougout
                }
            });
            this.setState({ authenticationTypesProviderButtons: this.getAuthenticationTypesProviderButtons() });
            this.setState({ authenticationTypesDropDownListItems: this.getAuthenticationTypesProviderList() });
            this.setState({
                selectedProvider: provider
            });
            this.setState({
                defaultAuthEmbeddedViewsRadioButtonIdx: this.state.defaultAuthEmbeddedViewsButtons.findIndex(button =>
                    button.value === this.state.siteAuthenticationSettings.providerIdForEmbeddedViews)
            });
            this.setState({ isExternalProviderEnabled: !!_.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => provider.isEnabled) });

            this.setState({
                authenticationTypesRadioButtonIdx: this.state.authenticationTypesProviderButtons.findIndex(button =>
                    button.value === (provider ? provider.id : ''))
            });
            const salesForceProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider as IOIDCAuthenticationProvider).alias === 'Salesforce') as IOIDCAuthenticationProvider;
            this.setState({savedSalesforceDomain: salesForceProvider?.salesforceDomain});
            this.setState({showSalesforceDomain: false});
            this.setState({isSamlMetadataFileChanged: false});
            this.setupMessageListener();
        });
    }

    enableProvider(id: string, idpConfigurationId: string) {
        return this.showEnableProviderWarning(id)
            .then(() => FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? SiteAuthenticationSettingsService.enableProviderForMultipleIdP(id, idpConfigurationId) : SiteAuthenticationSettingsService.enableProvider(id, idpConfigurationId))
            .then(() => {
                SiteAuthenticationSettingsService.getSiteAuthenticationSettings().then((siteAuthenticationSettings: ISiteAuthenticationSettings) => {
                    this.setState({ siteAuthenticationSettings: siteAuthenticationSettings });
                    this.setState({ isExternalProviderEnabled: is3rdParty(id) });
                    this.setState({
                        authenticationTypesRadioButtonIdx: this.state.authenticationTypesProviderButtons.findIndex(button =>
                            button.value === id)
                    });
                    this.setState({ selectedProvider: _.find(siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled)) });
                    this.setState({ defaultAuthEmbeddedViewsButtons: this.getProviderButtons() });
                });
            });
    }

    changeProvider(id: string) {
        this.setState({ isExternalProviderEnabled: is3rdParty(id) });
        this.setState({ selectedProvider: _.find(this.state.siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === id)) },
        () => {
            if (this.isSelectedProviderTypeOIDC()) {
                this.setState({ oidcProvider: _.find(this.state.siteAuthenticationSettings?.externalProviders, (provider: IAuthenticationProvider) => (provider.id === id)) as IOIDCAuthenticationProvider });
                const oidcProviderProps = this.state.oidcProviderProps;
                oidcProviderProps.provider = this.state.selectedProvider as IOIDCAuthenticationProvider;
                this.setState({ oidcProviderProps: oidcProviderProps });
            }
            if (FeatureFlags.adminLockoutEnabled && id === 'SAML') {
                this.setState({ showEditConnection: true });
            }
            if (this.isGenericOIDCSelected()) {
                this.setState({ showOIDCConfiguration: true});
            }
        });
    }

    showEnableProviderWarning(id: string): Promise<any> {
        return SiteUsers.instance.getById(getSessionManagementServiceInstance().sessionUserInfo().id).then((me: ISiteUser) => {
            this.setState({
                aboutToBeDisabled: _.filter(
                    this.state.siteAuthenticationSettings.externalProviders,
                    (provider: IAuthenticationProvider) => provider.isEnabled && provider.id !== id)
            });
            this.setState({
                aboutToLockMyselfOut: !!_.find(
                    this.state.aboutToBeDisabled,
                    (provider: IAuthenticationProvider) => !!me && provider.id === me.authenticationProviderId)
            });
        }).then(() => {
            if (this.state.aboutToBeDisabled.length === 0) {
                return Promise.resolve();
            }

            return openReactConfirmationDialog({
                title: Messages.ssoBeforeYouContinue_title(),
                message: '',
                paramsForAction: undefined,
                action: () => {
                    return Promise.resolve();
                },
                actionName: Messages.continueMessage(),
                dialogWidth: TabDialogWidth.width375px,
                bodyComponent: this.renderConfirmActionDialogSiteAuthenticationProviderEnable.bind(this)
            }).result;
        });
    }

    private updateSiteSettingsForSiteAdminRefreshTokenEnabled(checked: boolean) {
        const siteSettingsForSiteAdmin = this.props.siteSettingsForSiteAdmin;
        const siteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
        siteAdminSettings.refreshTokenEnabled = checked;
        const params: IUpdateSiteSettingsForSiteAdminParams = {
            id: (siteSettingsForSiteAdmin as any).id,
            siteAdminSettings: siteAdminSettings,
            subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
        };

        // Force a rerender since change comes from outside request, not state; too slow otherwise
        return this.notifyOnFail(WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params))).then(() => this.forceUpdate());
    }

  private updateSiteSettingsForAttributeCaptureEnabled(checked: boolean) {
    const siteSettingsForSiteAdmin = this.props.siteSettingsForSiteAdmin;
    const siteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
    siteAdminSettings.attributeCaptureEnabled = checked;
    const params: IUpdateSiteSettingsForSiteAdminParams = {
      id: (siteSettingsForSiteAdmin as any).id,
      siteAdminSettings: siteAdminSettings,
      subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
    };

    // Force a rerender since change comes from outside request, not state; too slow otherwise
    return this.notifyOnFail(WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params))).then(() => this.forceUpdate());
  }

    private notifyOnFail<TResult>(result: Promise<TResult>): Promise<TResult> {
        result.catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
        });
        return result;
    }

    // Callback functions for child components to use

    private updateEmbeddedOption = (allow: boolean) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.allowEmbeddedAuthentication = allow;
        this.setState({ samlProvider: samlProvider });
        if (!FeatureFlags.adminLockoutEnabled) {
            // watchWithUndo equivalent: make call to service when callback is triggered by child component
            SiteAuthenticationSettingsService.setAllowEmbeddedAuthentication(samlProvider.id, allow, samlProvider.idpConfigurationId);
        }
    };

    private allFieldsAreFilledOut(): boolean {
        return ((this.state.samlProvider?.identityProviderEntityId && !!this.state.samlMetadataFile) &&
            !!this.state.samlProvider?.attributeMapping?.emailMapping &&
            (this.state.samlProvider?.attributeMapping?.useFullName ? !!this.state.samlProvider?.attributeMapping?.fullNameMapping
            : (!!this.state.samlProvider?.attributeMapping?.firstNameMapping && !!this.state.samlProvider?.attributeMapping?.lastNameMapping)));
    }

    private changesArePresent(): boolean {
        // check if samlProvider is different (only checks ssoServiceURL and idp EntityID)
        const internalProvider = _.find(this.state.siteAuthenticationSettings?.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider;
        return !_.isEqual(internalProvider, this.state.samlProvider) || this.state.isSamlMetadataFileChanged;
    }

    private isConnectionUsable(): boolean {
        return !!(_.find(this.state.siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider).identityProviderEntityId;
    }

    private updateAllowFullName = (allow: boolean) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.useFullName = allow;
        this.setState({ samlProvider: samlProvider });
    };

    private updateFirstNameMapping = (name: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.firstNameMapping = name;
        this.setState({ samlProvider: samlProvider });
    };

    private updateLastNameMapping = (name: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.lastNameMapping = name;
        this.setState({ samlProvider: samlProvider });
    };

    private updateFullNameMapping = (name: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.fullNameMapping = name;
        this.setState({ samlProvider: samlProvider });
    };

    private updateEmailMapping = (email: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.emailMapping = email;
        this.setState({ samlProvider: samlProvider });
    };

    private updateSAMLMetadataFile = (file: File, identityProviderEntityId: string, ssoServiceURL: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.identityProviderEntityId = identityProviderEntityId;
        samlProvider.ssoServiceURL = ssoServiceURL;
        this.setState({ samlProvider: samlProvider, samlMetadataFile: file, isSamlMetadataFileChanged: true });
    };

    private saveSAMLAuthenticationProviderChanges() {
        this.sendSaveRequests().then(() => {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.siteAuthenticationSetToSAML() });
            ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteAuthenticationTestConnectionDesc2()});
            this.setupInitialState(true);
        });
    }

    private sendSaveRequests(): Promise<void> {
        const enableProvider2Promise = FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? SiteAuthenticationSettingsService.enableProviderForMultipleIdp2(this.state.samlProvider?.id, this.state.samlProvider?.idpConfigurationId)  :SiteAuthenticationSettingsService.enableProvider2(this.state.samlProvider?.id);
        return enableProvider2Promise.then(() => {
            return this.handleUpdate(SiteAuthenticationSettingsService.createOrUpdateSAMLConfiguration(this.state.samlProvider, this.state.samlMetadataFile));
        });
    }

    private updateSamlProvider = () => {
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings(true).then((settings: ISiteAuthenticationSettings) => {
            const updatedSamlProvider = _.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider;
            const newChanges = { ...updatedSamlProvider, attributeMapping: { ...updatedSamlProvider.attributeMapping } };
            this.setState({
                samlProvider: newChanges,
                samlProviderProps: {
                    ...this.state.samlProviderProps,
                    provider: newChanges
                },
            });
        });
    };

    private updateOIDCClientId = (clientId: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.clientId = clientId;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCClientSecret = (clientSecret: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.clientSecret = clientSecret;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCConfigurationUrl = (url: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.configurationUrl = url;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCEmailMapping = (claim: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.emailMapping = claim;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCUseFullName = (useFullName: boolean) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.useFullName = useFullName;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCFullNameMapping = (claim: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.fullNameMapping = claim;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCFirstNameMapping = (claim: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.firstNameMapping = claim;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCLastNameMapping = (claim: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.lastNameMapping = claim;
        this.setState({ oidcProvider: oidcProvider});
        this.oidcHasUpdated();
    };

    private updateOIDCSingleLougout = (enable: boolean) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.isLogoutSupported = enable;
        this.setState({ oidcProvider: oidcProvider });
        this.oidcHasUpdated();
    };

    private updateOIDCEmbeddedOption = (allow: boolean) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.allowEmbeddedAuthentication = allow;
        this.setState({ oidcProvider: oidcProvider });
        this.oidcHasUpdated();
    };

    // This should be called if generic OIDC config has been edited.
    private oidcHasUpdated() {
        let oidcProviderProps = this.state.oidcProviderProps;
        oidcProviderProps.hasUpdated = true;
        this.setState({ oidcProviderProps: oidcProviderProps });
    }

    private updateOIDCSalesforceDomain = (domain: string) => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.salesforceDomain = domain;
        this.setState({ oidcProvider: oidcProvider });
    };

    private setDefaultAuthenticationTypeAndUpdateRadioButtonIndex(id: string) {
        this.notifyOnFail(SiteAuthenticationSettingsService.setProviderIdForEmbeddedViews(id).then(() => {
            this.setState({ defaultAuthEmbeddedViewsRadioButtonIdx: this.state.defaultAuthEmbeddedViewsButtons.findIndex(button => button.value === id) });
        }));
    }

    private renderConfirmActionDialogSiteAuthenticationProviderEnable(): JSX.Element {
        return (
            <ConfirmActionDialogSiteAuthenticationProviderEnable aboutToBeDisabled={this.state.aboutToBeDisabled} aboutToLockMyselfOut={this.state.aboutToLockMyselfOut}
                getProviderDisplayName={this.state.getProviderDisplayName.bind(this)} getHelp={this.state.getHelp.bind(this)} />
        );
    }

    private handleUpdate(promise: Promise<any>): Promise<any> {
        promise.catch((reason: number) => {
          let translateKey = Messages.unexpectedError();
          if (reason === CommonErrorCode.InsecureSAMLEndpoint) {
            translateKey = Messages.siteAuthenticationIdPImportFailureInsecureEndpoint(this.translateValues);
          } else if (reason === CommonErrorCode.InvalidSAMLIdpMetadata) {
            translateKey = Messages.siteAuthenticationIdPImportFailureInvalidMetadata();
          }
          ToasterService.instance.pop({ type: ToastType.Error, text: translateKey});
        });
        return promise;
    }

    getProviderButtons(): RadioButtonWithLabelOption<string>[] {
        const providerButtons: RadioButtonWithLabelOption[] = [
            {
                label: Messages.userAuthSetting_AllowChoice(),
                testId: 'authentication-type-user-option-button',
                disabled: !this.state.isExternalProviderEnabled && !this.state.siteAuthenticationSettings.tabIdMfaProvider.isEnabled,
                value: undefined as any
            }
        ];

        if (this.state.siteAuthenticationSettings.tabIdMfaProvider.isEnabled) {
            providerButtons.push(
                {
                    label: this.state.getProviderDisplayName(this.state.siteAuthenticationSettings.tabIdMfaProvider),
                    testId: 'authentication-type-tableauid-mfa-button',
                    value: this.state.siteAuthenticationSettings.tabIdMfaProvider.id
                }
            );
            // When MFA is enabled, enable vanilla TableauID only when MFA is not required for the whole site
            if (this.state.siteAuthenticationSettings.defaultProvider.isEnabled) {
                providerButtons.push(
                    {
                        label: this.state.getProviderDisplayName(this.state.siteAuthenticationSettings.defaultProvider),
                        testId: 'authentication-type-tableauid-button',
                        value: this.state.siteAuthenticationSettings.defaultProvider.id
                    }
                );
            }
        } else {
            providerButtons.push(
                {
                    label: this.state.getProviderDisplayName(this.state.siteAuthenticationSettings.defaultProvider),
                    testId: 'authentication-type-tableauid-button',
                    value: this.state.siteAuthenticationSettings.defaultProvider.id
                }
            );
        }

        if (FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
          this.state.siteAuthenticationSettings?.allProviders.forEach((provider) => {
            // TODO: W-15467995 - [Multiple external IdP] List all external auth option for embedded views
            if (provider.isEnabled && (provider.idpConfigurationId === DEFAULT_IDP_CONFIG_ID_OIDC || provider.idpConfigurationId === DEFAULT_IDP_CONFIG_ID_SAML)) {
              providerButtons.push(
                {
                  label: provider.idpConfigurationName,
                  testId: 'authentication-type-sso-button',
                  value: provider.idpConfigurationId
                }
              );
            }
          });
        } else {
            this.state.siteAuthenticationSettings?.externalProviders.forEach((provider) => {
              if (provider.isEnabled) {
                providerButtons.push(
                  {
                    label: this.state.getProviderDisplayName(provider),
                    testId: 'authentication-type-sso-button',
                    value: provider.id
                  }
                );
              }
            });
        }

        return providerButtons;
    }

    private getAuthenticationTypesProviderList(): ListItem[] {
        const providerItems: ListItem[] = [];

        this.state.siteAuthenticationSettings.externalProviders.forEach((provider) => {
            const externalProviderButtonDetail =
                provider.type === AuthenticationProviderType.oidc && (provider as IOIDCAuthenticationProvider).alias === 'Salesforce'
                    ? <div style={dropDownWidgetDescriptionText}><div>{Messages.ssoAuthTypeOIDC_description_salesforce({ DOMAIN: (provider as IOIDCAuthenticationProvider)?.salesforceDomain || SALESFORCE_DOMAIN_PLACEHOLDER })}</div></div>
                    : <div style={dropDownWidgetDescriptionText} dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(provider)) }} />;

            providerItems.push(
                {
                    content:
                        <div style={dropDownWidgetListItem}>
                            <div style={dropDownWidgetTitleText}>
                                {this.state.getProviderDisplayName(provider)}
                            </div>
                            {externalProviderButtonDetail}
                        </div>,
                    testId: 'authentication-type-sso-button',
                    value: provider.id
                }
            );
        });
        return providerItems;
    }

    private getAuthenticationTypesProviderButtons(): RadioButtonWithLabelOption<string>[] {
        const providerButtons: RadioButtonWithLabelOption<string>[] = [];

        this.state.siteAuthenticationSettings.externalProviders.forEach((provider) => {
            const externalProviderButtonDetail =
                provider.type === AuthenticationProviderType.oidc && (provider as IOIDCAuthenticationProvider).alias === 'Salesforce'
                    ? <SalesforceProviderConfigurer provider={provider as IOIDCAuthenticationProvider} />
                    : <div dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(provider)) }} />;

            providerButtons.push(
                {
                    label: this.state.getProviderDisplayName(provider),
                    detail: externalProviderButtonDetail,
                    testId: 'authentication-type-sso-button',
                    value: provider.id
                }
            );
        });

        return providerButtons;
    }

    private hasMaxLimitReachedForIdpConfigurations(): boolean {
        return this.state.siteAuthenticationSettings?.allProviders?.length >= MAX_LIMIT_ON_TOTAL_ALLOWED_CONFIGURATIONS;
    }

    // Main render functions

    private renderAuthenticationTypes(): JSX.Element {
        let defaultSelectedProvider: IAuthenticationProvider = null;
        if (this.state.siteAuthenticationSettings !== undefined) {
            defaultSelectedProvider =
                (this.state.siteAuthenticationSettings.tabIdMfaProvider.isEnabled
                    && !this.state.siteAuthenticationSettings.defaultProvider.isEnabled)
                        ? this.state.siteAuthenticationSettings.tabIdMfaProvider
                        : this.state.siteAuthenticationSettings.defaultProvider;
        }
        return (
            <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.ssoAuthenticationTypes()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-msg">
                        {FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? Messages.siteAuthenticationMethodDesc() : Messages.ssoAuthenticationTypes_description2()}
                        &nbsp;
                        <TextLinkWidget onClick={() => this.state.getHelp('authenticationtypes')} testId='learn-more-link'>
                            {Messages.ssoLearnMore()}
                        </TextLinkWidget>
                    </div>
                    {/* TODO: I'm currently passing the default provider, and all of the external provider types as placeholders just to see if the UI is rendering correctly
                                Once we add in the new APIs, I'm guessing we'll want to create a new ISiteAuthenticationSettings object to represent all of a the site's configurations? Then
                                We'll pass the new object in here.  */}
                    {FeatureFlags.featureFlagForMultipleIDPSupportEnabled ?
                        <div>
                            <div style={samlProviderGroupSettings}>
                                <ButtonWidget
                                className="tb-float-left"
                                buttonType={ButtonType.Outline}
                                onClick={this.newConfigurationRedirect.bind(this)}
                                testId="new-configuration-button">
                                    {Messages.siteAuthenticationNewLoginConfiguration()}
                                </ButtonWidget>
                            </div>
                            <div className="tb-data-grid">
                                <AuthenticationTypesGrid displayData={this.state.siteAuthenticationSettings?.allProviders}
                                    currentColumnSortOrder={{
                                        field: AuthenticationColumnKeys.AuthenticationName,
                                        ascending: true
                                    }}
                                    handleColumnSortOrderChange={() => {}}
                                    isExternalProviderEnabledChanged={() => this.state.isExternalProviderEnabledChanged()}
                                />
                            </div>
                        </div>
                    : this.renderSingleIdP()}
                </div>
            </div>
        );
    }

    private renderSingleIdP(): JSX.Element {
        const isGenericOIDC = this.isGenericOIDCSelected();
        const isSAMLSelected = this.state.selectedProvider?.id === 'SAML';
        const enabledProvider = _.find(this.state.siteAuthenticationSettings?.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled));
        return (
            <div>
                <div className="tb-settings-control">
                    <CheckBoxWithLabelWidget
                        label={Messages.ssoEnableAnAdditional()}
                        testId='enable-additional-authentication-checkbox'
                        checked={this.state.isExternalProviderEnabled}
                        removeLeftMargin={true}
                        handleChange={() => this.state.isExternalProviderEnabledChanged()} />
                </div>
                {this.state.isExternalProviderEnabled ?
                    <div>
                        <div className="tb-settings-control">
                            <div>
                                <div style={{maxWidth: 300}}>
                                <SingleSelectDropdown
                                    list={<List items={this.state.authenticationTypesDropDownListItems} style={{maxWidth: 330}}/>}
                                    onChange={this.changeProvider.bind(this)}
                                    value={this.state.selectedProvider?.id}
                                    label='Authentication'
                                    type={DropdownButtonType.Outline}
                                    style={authDropdownStyle}
                                    testId='authentication-dropdown'>
                                {this.state.selectedProvider ? this.state.getProviderDisplayName(this.state.selectedProvider) : Messages.siteAuthenticationDropDownLabel()}
                                </SingleSelectDropdown>
                                </div>
                                {this.state.samlProviderProps.provider !== undefined && isSAMLSelected
                                    ?
                                        <div>
                                            <ExpandoWidget
                                                // Placed separately from button label to be interactive, since labels cannot be without selecting the option
                                                style={expandoStyle2}
                                                label={Messages.siteOIDCConfigurationRequired()}
                                                testId={'edit-connection-toggle'}
                                                isExpanded={this.state.showEditConnection}
                                                onExpand={() => this.setState({ showEditConnection: !this.state.showEditConnection })}
                                                onCollapse={() => this.setState({ showEditConnection: !this.state.showEditConnection })}
                                                direction={ExpandoDirection.Down}>
                                                <SAMLAuthenticationProvider { ...this.state.samlProviderProps }
                                                                            samlMetaDataFile={ this.state.samlMetadataFile }
                                                                            changesArePresent={ this.changesArePresent() } />
                                            </ExpandoWidget>
                                            {FeatureFlags.adminLockoutEnabled && (this.isCancelDisabled() && this.isSaveDisabled()) ?
                                                <div>
                                                    <div>{this.renderTestConnectionButton()}</div>
                                                    <div>{this.renderStep7AdminLockout()}</div>
                                                </div>
                                            : null}
                                        </div>
                                    : null}
                                {isGenericOIDC ?
                                    <div>
                                        <ExpandoWidget
                                            style={expandoStyle2}
                                            label={Messages.siteOIDCConfigurationRequired()}
                                            isExpanded={this.state.showOIDCConfiguration}
                                            onExpand={this.toggleShowOIDCConfiguration}
                                            onCollapse={this.toggleShowOIDCConfiguration}
                                            direction={ExpandoDirection.Down}>
                                            <OIDCAuthenticationProvider {...this.state.oidcProviderProps} />
                                        </ExpandoWidget>
                                        {this.state.selectedProvider.isEnabled ?
                                            <div>{this.renderTestConnectionButton()}</div>
                                        : null}
                                    </div>
                                : null}
                                {this.state.selectedProvider?.type === AuthenticationProviderType.oidc && (this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'Salesforce' ?
                                <div>
                                    <ExpandoWidget
                                        style={expandoStyle2}
                                        label={Messages.salesforceEditMyDomainOptional()}
                                        isExpanded={this.state.showSalesforceDomain}
                                        onExpand={this.toggleShowSalesforceDomain}
                                        onCollapse={this.toggleShowSalesforceDomain}
                                        direction={ExpandoDirection.Down}>
                                        <div>
                                            <TextFieldWidget
                                                kind='line'
                                                label={Messages.salesforceMyDomain()}
                                                onChange={(evt) => this.updateOIDCSalesforceDomain((evt.target.value))}
                                                onClear={() => this.updateOIDCSalesforceDomain(SALESFORCE_DOMAIN_PLACEHOLDER)}
                                                required={false}
                                                placeholder={SALESFORCE_DOMAIN_PLACEHOLDER}
                                                style={{ width: 270 }}
                                                testId='salesforce-domain-field-text'
                                                defaultValue={this.state.oidcProvider?.salesforceDomain || ''}
                                            />
                                        </div>
                                    </ExpandoWidget>
                                </div>
                                : null}
                                {this.isANewAuthenticationProviderSelected() && !!enabledProvider ?
                                    <div style={{ paddingBottom: '16px' }}>
                                        <CompactInlineAlertWidget type={InlineAlertType.Warning} testId='' style={{ paddingTop: '8px' }}>
                                            <div style={{ paddingBottom: '12px' }}>
                                                <div>
                                                    {Messages.ssoBeforeYouContinue_text_3({
                                                        PREVIOUS_AUTH_METHOD: this.state.getProviderDisplayName(enabledProvider),
                                                        NEW_AUTH_METHOD: this.state.getProviderDisplayName(this.state.selectedProvider),
                                                    })}
                                                </div>
                                                <p></p>
                                                <p></p>
                                                <div>
                                                    {Messages.ssoBeforeYouContinue_text_4()}
                                                </div>
                                            </div>
                                        </CompactInlineAlertWidget>
                                    </div>
                                : null}
                            </div>
                        </div>
                        <div className="tb-settings-control">
                            <span className={UserSettingsStyle.cancelButton}>
                                <ButtonWidget
                                    type="reset"
                                    buttonType={ButtonType.Outline}
                                    onClick={this.cancelOnClick.bind(this)}
                                    disabled={this.isCancelDisabled()}
                                >
                                    {Messages.cancel()}
                                </ButtonWidget>
                            </span>
                            <span>
                                <ButtonWidget
                                    type="submit"
                                    buttonType={ButtonType.Primary}
                                    onClick={this.saveChangesOnClick.bind(this)}
                                    disabled={this.isSaveDisabled()}
                                    testId={'save-changes-button'}
                                >
                                    {Messages.saveChanges()}
                                </ButtonWidget>
                            </span>
                        </div>
                    </div>
                : null}
            </div>
        );
    }

    private isSaveDisabled(): boolean {
        if (this.isGenericOIDCSelected()) {
            return !this.canSaveOIDCProvider() ;
        } else if (this.state.selectedProvider?.id === 'SAML') {
            return !(this.allFieldsAreFilledOut() && (this.changesArePresent() || this.isANewAuthenticationProviderSelected()));
        } else if ((this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'Salesforce') {
            return (this.state.oidcProvider.salesforceDomain === this.state.savedSalesforceDomain) && !this.isANewAuthenticationProviderSelected();
        }
        return !this.isANewAuthenticationProviderSelected();
    }

    private isCancelDisabled(): boolean {
        if (this.isGenericOIDCSelected()) {
            return !this.state.oidcProviderProps.hasUpdated && !this.isANewAuthenticationProviderSelected();
        } else if (this.state.selectedProvider?.id === 'SAML') {
            return !(this.changesArePresent() || this.isANewAuthenticationProviderSelected());
        } else if ((this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'Salesforce') {
            return (this.state.oidcProvider.salesforceDomain === this.state.savedSalesforceDomain) && !this.isANewAuthenticationProviderSelected();
        }
        return !this.isANewAuthenticationProviderSelected();
    }

    private isANewAuthenticationProviderSelected(): boolean {
        const enabledProvider = _.find(this.state.siteAuthenticationSettings?.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled));
        return !(enabledProvider?.type === this.state.selectedProvider?.type &&
            (this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === (enabledProvider as IOIDCAuthenticationProvider)?.alias);
    }

    private saveChangesOnClick = () => {
        if (this.isSelectedProviderTypeOIDC()) {
            this.saveChangesOnClickOIDC();
        } else if (this.state.selectedProvider?.id === 'SAML') {
            this.saveSAMLAuthenticationProviderChanges();
        }
    };

    private toggleShowOIDCConfiguration = () => this.setState({ showOIDCConfiguration: !this.state.showOIDCConfiguration});

    private toggleShowSalesforceDomain = () => this.setState({ showSalesforceDomain: !this.state.showSalesforceDomain});

    private isSelectedProviderTypeOIDC(): boolean {
        return this.state.selectedProvider?.type === AuthenticationProviderType.oidc;
    }

    private isGenericOIDCSelected(): boolean {
        return this.state.selectedProvider?.type === AuthenticationProviderType.oidc
        && (this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'OpenId Connect';
    }

    private canSaveOIDCProvider(): boolean {
        const oidcProvider = this.state.oidcProvider;
        if (oidcProvider == null) {
            return false;
        }
        if (oidcProvider.alias !== 'OpenId Connect') {
            return true;
        }
        if (!this.state.oidcProviderProps.hasUpdated) {
            return false;
        }
        if (oidcProvider.clientId != null && oidcProvider.clientId.length !== 0 &&
            oidcProvider.clientSecret != null && oidcProvider.clientSecret.length !== 0 &&
            oidcProvider.configurationUrl != null && oidcProvider.configurationUrl.length !== 0) {
            return true;
        }
    }

    private saveChangesOnClickOIDC = () => {
        let oidcProvider = this.state.oidcProvider;
        oidcProvider.isEnabled = true;
        const enableProvider2Promise = FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? SiteAuthenticationSettingsService.enableProviderForMultipleIdp2(oidcProvider.id, oidcProvider.idpConfigurationId) : SiteAuthenticationSettingsService.enableProvider2(oidcProvider.id);
        enableProvider2Promise.then(() => {
            return SiteAuthenticationSettingsService.saveOIDCProvider(oidcProvider);
        }).then((result) => {
            if (result.errors && result.errors.length) {
                throw new Error();
            }
            const provider = oidcProvider.id === 'OIDC' ? oidcProvider.id : oidcProvider.alias;
            if (this.isGenericOIDCSelected()) {
                ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteOIDCConfigurationToastForTest() });
            }
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.siteOIDCConfigurationToastForSave({PROVIDER: provider}) });

            const oidcProviderProps = this.state.oidcProviderProps;
            oidcProviderProps.showTestConfiguration = true;
            oidcProviderProps.hasUpdated = false;
            this.setState({ oidcProviderProps: oidcProviderProps });
            if (!this.state.showOIDCConfiguration) {
                this.toggleShowOIDCConfiguration();
            }
        }).then(() => {
            this.setupInitialState(true);
        }).catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
        });
    };

    private cancelOnClick = () => {
        const refresh = !(this.state.selectedProvider?.id === 'SAML');
        this.setupInitialState(refresh);
        if (this.state.showOIDCConfiguration) {
            this.toggleShowOIDCConfiguration();
        }
    };

    private renderStep7AdminLockout(): JSX.Element {
        return (
            <div>
                <div style={samlProviderTextTitleStyle}>
                    <span>{Messages.siteAuthenticationStep7()}</span>
                </div>
                <div>
                    <div style={{...samlProviderGroupContent, ...samlProviderTextDescriptionStyle}}>
                        {Messages.siteAuthenticationTroubleshootActions2()}
                    </div>
                    <ul style={ {paddingLeft: '20px'} }>
                        <li style={samlProviderTextDescriptionStyle}>
                            <span style={samlProviderMatchAttributesTitleStyle}>{Messages.siteAuthenticationTroubleshootAssertionMatchingTitle()}</span>
                            &nbsp;
                            <span>{Messages.siteAuthenticationTroubleshootAssertionMatchingDescription()}</span>
                        </li>
                        <div style={{paddingTop: '16px', paddingBottom: '16px'}}>
                            <ButtonWidget
                                buttonType={ButtonType.Outline}
                                onClick={(evt) => this.downloadLog(evt)}
                                testId="download-troubleshooting-log-button">
                                {Messages.siteAuthenticationDownloadLog()}
                            </ButtonWidget>
                        </div>
                        <li style={samlProviderTextDescriptionStyle}>
                            <span style={samlProviderMatchAttributesTitleStyle}>{Messages.siteAuthenticationTroubleshootOption1Title()}</span>
                            &nbsp;
                            <span>{Messages.siteAuthenticationTroubleshootOption1Description()}</span>
                        </li>
                        <li style={samlProviderTextDescriptionStyle}>
                            <span style={samlProviderMatchAttributesTitleStyle}>{Messages.siteAuthenticationTroubleshootOption2Title()}</span>
                            &nbsp;
                            <span>{Messages.siteAuthenticationTroubleshootOption2Description()}</span>
                        </li>
                    </ul>
                </div>
            </div>
        );
    }

    private renderTestConnectionButton(): JSX.Element {
        return(
            <div>
                <div style={samlProviderGroupSettings}>
                    <CompactInlineAlertWidget type={InlineAlertType.Info} testId='' style={samlProviderAlertText}>
                        {<div>{Messages.siteAuthenticationTestConnectionRecommendation()}</div>}
                    </CompactInlineAlertWidget>
                </div>
                <div style={samlProviderGroupSettings}>
                    <div style={samlProviderTextTitleStyle}>
                        {this.isGenericOIDCSelected() ? <span style={samlProviderGroupNumber}>6.</span> : <span style={samlProviderGroupNumber}>7.</span>}
                        &nbsp;
                        <span>{Messages.siteAuthenticationTestLoginConfigurationRecommended()}</span>
                    </div>
                    <div>
                        <div style={samlProviderGroupContent}>
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationTestConfigurationDesc()}
                            </div>
                        </div>
                        <div style={samlProviderGroupSettings}>
                            <ButtonWidget
                            className="tb-float-left"
                            buttonType={ButtonType.Outline}
                            onClick={this.test.bind(this)}
                            testId="test-connection-button">
                                {Messages.siteAuthenticationTestLoginConfiguration()}
                            </ButtonWidget>
                            {this.state.userAttributes ?
                                <div className="tb-enabled-color">
                                    {Messages.testConnectionSuccess()}
                                </div>
                            : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private newConfigurationRedirect() {
        if (this.hasMaxLimitReachedForIdpConfigurations()) {
            MaxConfigurationsReachedDialog();
        } else {
            const urls = siteUrlsSelector(getAppStore().getState());
            const authUrl = urls.authenticationNewConfigurationUrl();
            Location.instance.setUrl(urls.authenticationNewConfigurationUrl());
        }
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private test() {
        WindowLocationService.open(this.state.selectedProvider.testLoginUrl,
          'testLogin', { left: 20, top: 20, width: 500, height: 600, toolbar: 1, resizable: 1 });
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private downloadUrl(evt: React.MouseEvent<HTMLButtonElement>, url: string) {
        evt.preventDefault();
        WindowLocationService.download(url);
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private downloadLog(evt: React.MouseEvent<HTMLButtonElement>) {
        this.downloadUrl(evt, WebHttpClient.FILE_UPLOAD_API_PREFIX + 'siteSAMLLog');
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private translateValues = {
        SERVER_LEVEL_AUTH: AuthenticationTypeResolutionService.siteAuthDefaultLabel(),
        SERVER_NAME: ServerSettingsUnauthenticated.serverName()
    };

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private setUserAttributes(userAttributes: IUserAttributes) {
        this.setState({userAttributes: userAttributes});
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private getHostName(url: string): string {
        // Can't use URL since it's not supported in IE (or node)
        const urlParsingNode = document.createElement('a');
        urlParsingNode.setAttribute('href', url);
        return urlParsingNode.hostname;
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private getBaseDomain(url: string): string {
        const hostName = this.getHostName(url);
        // If the host name is ip address, returns the ip address as the domain.
        if (this.isIPV4(hostName)) {
            return hostName;
        }

        const splitHost = hostName.split('.');
        const tldregex = new RegExp('(co|com|gov|gouv|net|org|edu|mil)');
        if (splitHost.length>=3 && tldregex.test(splitHost[splitHost.length-2])) {
            return splitHost.slice(-3).join('.');
        }
        else {
            return splitHost.slice(-2).join('.');
        }
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private isIPV4(host: string): boolean {
        const elements = host.split('.');
        if (elements.length === 4) {
            return elements.every((element) => {
                const value = parseInt(element, 10);
                return !isNaN(value) && value >=0 && value <= 255;
            });
        }

        return false;
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private isRunningUnderTest(): boolean {
        return window.hasOwnProperty('mocha');
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    public receiveMessage(evt: MessageEvent) {
        if (this.getHostName(evt.origin) !== this.getHostName(this.state.selectedProvider?.testLoginUrl) && !this.isRunningUnderTest()) {
          return;
        }
        const userAttributes: IUserAttributes = JSON.parse(evt.data);
        this.setUserAttributes(userAttributes);
        if (!this.isRunningUnderTest()) {
          // (TFS 700140) This line causes the Karma debug window to close, when run via the unit tests
          (evt.source as any).close();
        }
    }

    // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
    private setupMessageListener() {
        const baseDomain = this.getBaseDomain(WindowLocationService.getOrigin());
        const testLoginBaseDomain = this.getBaseDomain(this.state.selectedProvider?.testLoginUrl);

        // Enable cross-origin access to this window from the new window, provided they share the same base domain
        if (baseDomain === testLoginBaseDomain) {
            // Test login window is expected to postMessage bearing resolved UserDetails
            window.addEventListener('message', this.messageListener);
            if (!this.isRunningUnderTest()) {
                document.domain = baseDomain;
            }
        }
    }

    private renderDefaultAuthenticationTypeForEmbeddedViews(): JSX.Element {
        return (
            this.state.isEmbeddedVizForSiteSamlSupported
                ? <div className="tb-settings-group">
                    <div className="tb-settings-group-name">
                        {Messages.siteAuthenticationEmbeddedTypeDesc()}
                    </div>
                    <div className="tb-indent">
                        <div className="tb-settings-control">
                            <div className="tb-dialog-authentication-option">
                                <RadioButtonWithLabelGroupWidget
                                    ariaLabel={'defaultAuthenticationEmbeddedViews'}
                                    testId='default-authentication-embedded-views-buttons'
                                    buttonList={this.state.defaultAuthEmbeddedViewsButtons}
                                    checkedIndex={this.state.defaultAuthEmbeddedViewsRadioButtonIdx}
                                    handleChange={this.setDefaultAuthenticationTypeAndUpdateRadioButtonIndex.bind(this)} />
                            </div>
                        </div>
                    </div>
                </div>
                : null);
    }

    private renderScimSettings(): JSX.Element {
        return (
            <ScimSettings />
        );
    }

    private renderConnectedClients(): JSX.Element {
        return (
            this.state.isConnectedClientsVisible
                ? <div className="tb-settings-group">
                    <div className="tb-settings-group-name">
                        {Messages.siteAuthenticationConnectedClients()}
                    </div>
                    <div className="tb-indent">
                        <div className="tb-settings-control">
                            <div className="tb-settings-msg">
                                {Messages.siteAuthenticationConnectedClientsDescription()}
                            </div>
                        </div>
                        {this.props.siteSettingsForSiteAdmin !== undefined
                            ? <CheckBoxWithLabelWidget
                                label={Messages.siteAuthenticationAllowConnectedClients()}
                                checked={(this.props.siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings).refreshTokenEnabled}
                                testId={'allow-connected-clients-checkbox'}
                                removeLeftMargin={true}
                                handleChange={this.updateSiteSettingsForSiteAdminRefreshTokenEnabled.bind(this)} />
                            : null}
                    </div>
                </div>
                : null
        );
    }

  private renderAttributeCapture(): JSX.Element {
    return (
      FeatureFlags.userAttributeFunctionsEnabled
      ? <div className="tb-settings-group">
          <div className="tb-settings-group-name">
            {Messages.siteAuthenticationAttributeCapture()}
          </div>
          <div className="tb-indent">
            <div className="tb-settings-control">
              <div className="tb-settings-msg">
                {Messages.siteAuthenticationAttributeCaptureDescription()}
                <TextLinkWidget
                    testId={'user-attribute-description'}
                    target={'_blank'}
                    className={learnMoreStyle}
                    href={HelpService.getServerHelpUrlByCategory('user-attributes')}>
                    {Messages.learnMore()}
                </TextLinkWidget>
              </div>
            </div>
            {this.props.siteSettingsForSiteAdmin !== undefined
              ? <CheckBoxWithLabelWidget
                label={Messages.siteAuthenticationAllowAttributeCapture()}
                checked={(this.props.siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings).attributeCaptureEnabled}
                testId={'allow-attribute-capture-checkbox'}
                removeLeftMargin={true}
                handleChange={this.updateSiteSettingsForAttributeCaptureEnabled.bind(this)} />
              : null}
          </div>
        </div>
      : null
    );
  }

    public render(): JSX.Element {
        return (
            <div className="tb-scroll">
                <div className="tb-settings-container">
                    <div className="tb-settings-section">
                        <div className="tb-site-authentication">
                            {this.renderAuthenticationTypes()}
                            {this.renderDefaultAuthenticationTypeForEmbeddedViews()}
                            {this.renderAttributeCapture()}
                            {this.renderScimSettings()}
                            {this.renderConnectedClients()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
